import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Sparkles, BookOpen, Quote } from 'lucide-react';
import { fetchTheme } from '@/services/api';

interface YearThemeData {
    id?: number;
    year: string;
    theme: string;
    bible_reference: string;
    translation_en: string;
    translation_hebrew?: string;
    translation_greek?: string;
    translation_yoruba?: string;
    watchword: string;
    created_at?: string;
    updated_at?: string;
}


const YearTheme = () => {
    const [theme, setTheme] = useState<YearThemeData | null>(null);
    const [loading, setLoading] = useState(true);
    const currentYear = new Date().getFullYear();

    useEffect(() => {
        const loadTheme = async () => {
            setLoading(true)
            try {
                const res = await fetchTheme(currentYear);
                setTheme(res.data.data);
                console.log(res.data.data) // Because it's returned as an array
            } catch (err) {
                console.error('Failed to fetch theme info:', err);
            } finally {
                setLoading(false)
            }
        };
        loadTheme();
    }, []);

    if (loading || !theme) {
        return (
            <div className="text-center py-12 text-purple-600 animate-pulse font-semibold text-xl">
                Loading 2025 Theme...
            </div>
        );
    }

    return (
        <>
            <section className="bg-gradient-to-br from-purple-50 to-purple-100 py-24 px-6">
                <div className="max-w-4xl mx-auto bg-white border border-purple-200 rounded-2xl shadow-xl p-10 text-center space-y-6">

                    {/* Year and Theme Label */}
                    <div className="flex justify-center items-center gap-3 text-purple-600 text-lg font-medium">
                        <Sparkles className="w-6 h-6 text-purple-500" />
                        {theme.year} Diocesan Theme
                    </div>

                    {/* Main Translation (Hebrew or Greek) */}
                    {(theme.translation_hebrew || theme.translation_greek) && (
                        <>
                            <h1 className="text-5xl md:text-6xl font-extrabold text-purple-900 tracking-tight mb-3">
                                {theme.translation_hebrew || theme.translation_greek}
                            </h1>
                            <span className="text-grey-100">({theme.theme})</span>
                        </>
                    )}

                    {/* English Theme */}
                    <p className="text-2xl md:text-3xl text-purple-700 italic font-semibold">
                        “{theme.translation_en}”
                    </p>

                    {/* Yoruba Translation */}
                    {theme.translation_yoruba && (
                        <p className="text-purple-800 text-lg font-bold">“{theme.translation_yoruba}”</p>
                    )}

                    {/* Scripture Reference & Watchword */}
                    <div className="mt-6 space-y-3 text-purple-700">
                        <div className="flex justify-center items-center gap-2 font-semibold text-lg">
                            <BookOpen className="w-5 h-5 text-purple-500" />
                            {theme.bible_reference}
                        </div>

                        {theme.watchword && (
                            <blockquote className="relative max-w-2xl mx-auto text-md italic text-purple-600">
                                <Quote className="absolute -left-4 top-0 w-6 h-6 text-purple-300" />
                                <span className="pl-6 block">{theme.watchword}</span>
                            </blockquote>
                        )}
                    </div>
                </div>
            </section>
        </>
    );
};

export default YearTheme;
